import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { ArrowDown, Copy, Check } from 'lucide-react'
import { MessageScroller, useMessageScroller } from '@shadcn/react/message-scroller'
import MarkdownLite from '../MarkdownLite'
import type { ChatMessageData } from './chatModel'
import { ChatMessageNavigator } from './MessageNavigator'

// The scrolling message log. Scroll behaviour (follow-the-live-edge while streaming, release when the
// reader scrolls away, turn anchoring) is delegated to the shadcn message-scroller primitive
// (@shadcn/react) rather than a hand-rolled scrollIntoView. The scroll-to-latest button, however, is
// rendered locally (ChatScrollButton below): the primitive's own Button reveals from a store whose
// commits can go stale when the transcript grows without a scroll event. We render each
// row through MessageScroller.Item so the primitive can track turn boundaries; a row is either a chat
// message (user/assistant) or an arbitrary inline node (e.g. a QA-activity entry).
//
// The message layout is adapted from replayio/nut.new (UserMessage / AssistantMessage): wide rows
// (~95% width) rather than compact bubbles — the user turn is a right-aligned card on a subtle
// surface, the assistant turn is a left-aligned block of prose with no card chrome, and each exposes a
// copy button on hover. No avatars; the chat header already identifies the assistant.

// A single row in the log: a chat message, or arbitrary inline content (id + node).
export type ChatListItem = {
  id: string
  message?: ChatMessageData
  node?: ReactNode
  // Marks a turn boundary for the scroller, which parks a newly anchored turn near the top of the
  // viewport. Set it for turns that ARRIVE while the log is open, not for loaded history: the scroller
  // re-anchors to the first anchor it has never scrolled to whenever the row count is unchanged but the
  // rows themselves changed, so an anchored history turn is a standing invitation for some unrelated
  // update to yank the reader back to the oldest message in the conversation.
  anchor?: boolean
}

// Compact date/time shown next to the copy button (e.g. "Jul 12, 3:45 PM"). Returns null for a
// missing or unparseable timestamp so the copy button is still shown on its own.
function formatMessageTime(iso: string | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

// The hover copy button shown at the trailing edge of each message row.
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard unavailable — nothing to do */
    }
  }
  return (
    <button
      type="button"
      className="chat-msg__copy"
      onClick={copy}
      aria-label={copied ? 'Copied' : 'Copy message'}
      title={copied ? 'Copied' : 'Copy message'}
      data-testid="chat-msg-copy"
    >
      {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
    </button>
  )
}

function Message({ message, currentUserId }: { message: ChatMessageData; currentUserId?: string | null }) {
  const isUser = message.role === 'user'
  const time = formatMessageTime(message.createTime)
  // The transcript is shared across every member of the project, so a user turn from someone OTHER than
  // the current viewer is labelled with its author's name — that's what lets each member see who said
  // what. Own turns (and unattributed legacy/assistant messages) show no label.
  const authorLabel = isUser && message.authorId && message.authorId !== currentUserId
    ? (message.authorName || 'Team member')
    : null
  return (
    <div className={`chat-msg chat-msg--${message.role}`} data-testid={`chat-msg-${message.role}`}>
      {authorLabel && (
        <div className="chat-msg__author" data-testid="chat-msg-author">{authorLabel}</div>
      )}
      <div className="chat-msg__body">
        {isUser
          ? message.content
          : <MarkdownLite text={message.content} className="chat-msg__md" looseLists />}
      </div>
      <div className="chat-msg__actions">
        {time && (
          <time className="chat-msg__time" dateTime={message.createTime} data-testid="chat-msg-time">
            {time}
          </time>
        )}
        <CopyButton text={message.content} />
      </div>
    </div>
  )
}

// Scrolls the transcript to the live edge whenever `signal` changes to a new positive value. Lives
// inside MessageScroller.Provider so it can reach the imperative scroll API. Used to force the async,
// action-demanding notices (e.g. an exploration pausing for login) into view even when the reader has
// scrolled up — the scroller's normal auto-follow only fires when already parked at the bottom.
function ScrollToEndOnSignal({ signal }: { signal: number }) {
  const { scrollToEnd } = useMessageScroller()
  const prev = useRef(signal)
  useEffect(() => {
    if (signal > 0 && signal !== prev.current) scrollToEnd({ behavior: 'smooth' })
    prev.current = signal
  }, [signal, scrollToEnd])
  return null
}

// The scroll-to-latest button. The message-scroller's own Button derives data-active from an internal
// store that is only recommitted on viewport scroll events, direct childList changes of the
// transcript, and transcript/viewport box resizes — and while following the live edge it force-
// commits the snapshot to "at bottom". Rendered height that grows after the last commit (streamed
// text filling an already-mounted row, an image decoding) fires none of those while the reader sits
// parked near the bottom, so the button can stay hidden — opacity 0, pointer-events none, inert —
// long after the viewport has settled above the live edge (QA bug bug-mu13sb0s-aoqa: ~26 s, 76 px
// above the edge, until an unrelated re-render recomputed it). This button instead derives its state
// from the viewport's live distance to the bottom edge, recomputed on every trigger that can move
// it, so the reveal follows the real scroll position rather than the store's last commit.
const SCROLL_EDGE_THRESHOLD_PX = 8 // message-scroller's default scrollEdgeThreshold

// The live viewport/content refs are owned by ChatMessageList — it renders the MessageScroller
// Viewport/Content whose elements this button observes — and passed down by reference.
function ChatScrollButton({
  viewportRef,
  contentRef,
}: {
  viewportRef: RefObject<HTMLDivElement | null>
  contentRef: RefObject<HTMLDivElement | null>
}) {
  const { scrollToEnd } = useMessageScroller()
  // Whether the viewport has settled away from the live edge — read from the viewport's own scroll
  // metrics (scrollHeight is the browser's ground truth for the scrollable extent), not from the
  // message-scroller's committable snapshot.
  const [active, setActive] = useState(false)

  useEffect(() => {
    const viewport = viewportRef.current
    const content = contentRef.current
    if (!viewport || !content) return
    let frame = 0
    const recompute = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        setActive(
          viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight > SCROLL_EDGE_THRESHOLD_PX,
        )
      })
    }
    recompute()
    // Scroll and load events do not bubble, but a capture listener on the viewport still sees its
    // own scroll events — and descendants': inner scrollers, decoded images — on the way down.
    viewport.addEventListener('scroll', recompute, { capture: true })
    viewport.addEventListener('load', recompute, { capture: true })
    // Viewport resizes (layout) and transcript resizes (content growth) both move the metrics;
    // ResizeObserver also fires once on observe, covering the first mount.
    const resizes = new ResizeObserver(recompute)
    resizes.observe(viewport)
    resizes.observe(content)
    // Streaming into an already-mounted row (text nodes, nested elements) is invisible to
    // childList-only observers — watch the transcript subtree directly.
    const mutations = new MutationObserver(recompute)
    mutations.observe(content, { childList: true, subtree: true, characterData: true })
    return () => {
      cancelAnimationFrame(frame)
      viewport.removeEventListener('scroll', recompute, { capture: true })
      viewport.removeEventListener('load', recompute, { capture: true })
      resizes.disconnect()
      mutations.disconnect()
    }
    // viewportRef/contentRef are stable ref objects; listed only for exhaustive-deps.
  }, [viewportRef, contentRef])

  return (
    <button
      type="button"
      className="chat-scroll-btn"
      aria-label="Scroll to latest"
      data-active={active ? 'true' : 'false'}
      inert={!active}
      tabIndex={active ? undefined : -1}
      onClick={(e) => {
        scrollToEnd({ behavior: 'smooth' })
        e.currentTarget.blur()
      }}
    >
      <ArrowDown size={16} strokeWidth={2} aria-hidden />
    </button>
  )
}

export default function ChatMessageList({
  items,
  pending,
  typing,
  pendingLabel = 'Thinking…',
  emptyState,
  showMessageNavigator = false,
  historyLoading = false,
  scrollToEndSignal = 0,
  currentUserId,
}: {
  items: ChatListItem[]
  // A turn is in progress. The typing-indicator row stays mounted for the *whole* turn (kept but
  // hidden once the reply starts filling in). This is deliberate: if the row unmounted on the same
  // commit the first assistant token mounts its message row, the scroller's child count would be
  // net-unchanged, and the message-scroller treats a net-zero child change as "an existing turn
  // became active" — re-anchoring to the earliest not-yet-anchored user turn and yanking the view
  // back to a much older message. Holding the row mounted makes the pending→reply swap a genuine
  // child-count increase, which the scroller handles as an append (no spurious re-anchor).
  pending: boolean
  // Whether to show the animated typing dots. True only before the first streamed token; afterwards
  // the row stays mounted (see `pending`) but is hidden so the reply fills in place. Defaults to
  // `pending` when omitted.
  typing?: boolean
  pendingLabel?: string
  emptyState?: ReactNode
  // When true, render the nut.new-style message navigator above the transcript (ProjectChat).
  // Hides the floating scroll-to-latest button — the navigator owns jump-to-bottom instead.
  showMessageNavigator?: boolean
  // Whether the stored transcript is still being fetched, so the navigator can hold its slot open
  // rather than appearing above the log a moment later and pushing it down.
  historyLoading?: boolean
  // Bump to a new positive value to force the transcript to scroll to its live edge (used when an
  // exploration pauses for login and the notice + choices must be surfaced regardless of scroll pos).
  scrollToEndSignal?: number
  // The signed-in viewer's user id, so a user turn authored by someone else is labelled with their name
  // (the transcript is shared across the project's members). Own turns render unlabelled.
  currentUserId?: string | null
}) {
  // Live element refs for the transcript's viewport and content, attached to the MessageScroller
  // Viewport/Content below and observed by ChatScrollButton (the primitive forwards its ref prop to
  // the underlying div), which derives the reveal state from the real scroll metrics.
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)

  const isEmpty = items.length === 0 && !pending
  const showTyping = typing ?? pending

  return (
    <MessageScroller.Provider autoScroll defaultScrollPosition="end">
      <ScrollToEndOnSignal signal={scrollToEndSignal} />
      <MessageScroller.Root className="chat-scroller" data-testid="chat-log">
        {showMessageNavigator && <ChatMessageNavigator items={items} historyLoading={historyLoading} />}
        <MessageScroller.Viewport ref={viewportRef} className="chat-log" aria-label="Messages">
          <MessageScroller.Content ref={contentRef} className="chat-log__content">
            {isEmpty && emptyState}
            {items.map((it) => (
              <MessageScroller.Item
                key={it.id}
                messageId={it.id}
                scrollAnchor={it.anchor ?? false}
                className="chat-log__item"
              >
                {it.message ? <Message message={it.message} currentUserId={currentUserId} /> : it.node}
              </MessageScroller.Item>
            ))}
            {pending && (
              // `hidden` (not conditional render) once the reply starts, so the row stays a mounted
              // child of the scroller for the whole turn — see the `pending` prop note above.
              <MessageScroller.Item messageId="__pending" className="chat-log__item" hidden={!showTyping}>
                <div className="chat-pending" data-testid="chat-pending" aria-live="polite">
                  <span className="chat-typing" aria-hidden>
                    <span /><span /><span />
                  </span>
                  <span className="chat-pending__label">{pendingLabel}</span>
                </div>
              </MessageScroller.Item>
            )}
          </MessageScroller.Content>
        </MessageScroller.Viewport>
        {!showMessageNavigator && (
          <ChatScrollButton viewportRef={viewportRef} contentRef={contentRef} />
        )}
      </MessageScroller.Root>
    </MessageScroller.Provider>
  )
}
